import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useT } from '../i18n';

const STORAGE_KEY = 'alb_cookie_consent';
const CONSENT_VERSION = 1;

function readConsent() {
    try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return value?.version === CONSENT_VERSION ? value : null;
    } catch {
        return null;
    }
}

export function CookieConsent() {
    const t = useT();
    const [consent, setConsent] = useState(readConsent);
    const [showPreferences, setShowPreferences] = useState(false);
    const [analytics, setAnalytics] = useState(consent?.analytics || false);
    const [marketing, setMarketing] = useState(consent?.marketing || false);

    const saveConsent = (preferences) => {
        const nextConsent = {
            version: CONSENT_VERSION,
            necessary: true,
            analytics: !!preferences.analytics,
            marketing: !!preferences.marketing,
            updated_at: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConsent));
        window.dispatchEvent(new CustomEvent('alb-cookie-consent', { detail: nextConsent }));
        setConsent(nextConsent);
        setAnalytics(nextConsent.analytics);
        setMarketing(nextConsent.marketing);
        setShowPreferences(false);
    };

    const openPreferences = () => {
        setAnalytics(consent?.analytics || false);
        setMarketing(consent?.marketing || false);
        setShowPreferences(true);
    };

    if (consent && !showPreferences) {
        return (
            <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={openPreferences}
                className="fixed bottom-20 left-3 z-[70] border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] shadow-lg md:bottom-4"
                aria-label={t('cookies.preferences')}
                title={t('cookies.preferences')}
            >
                <Cookie />
            </Button>
        );
    }

    return (
        <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] p-4 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="cookie-title">
            <div className="mx-auto max-w-5xl">
                <div className="flex items-start gap-3">
                    <Cookie className="mt-1 h-5 w-5 shrink-0 text-[color:var(--alb-sun)]" />
                    <div className="min-w-0 flex-1">
                        <h2 id="cookie-title" className="font-display text-lg font-semibold">{t('cookies.title')}</h2>
                        <p className="mt-1 text-sm leading-6 text-[color:var(--alb-text-muted)]">
                            {t('cookies.description')} <Link to="/privacy" className="underline hover:text-white">{t('cookies.privacy_link')}</Link>.
                        </p>
                    </div>
                    {consent && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => setShowPreferences(false)} aria-label={t('cookies.close')}>
                            <X />
                        </Button>
                    )}
                </div>

                {showPreferences && (
                    <div className="mt-4 grid gap-3 border-y border-[color:var(--alb-hairline)] py-4 sm:grid-cols-3">
                        <div>
                            <div className="flex items-center justify-between gap-2"><strong className="text-sm">{t('cookies.necessary')}</strong><Switch checked disabled aria-label={t('cookies.necessary')} /></div>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">{t('cookies.necessary_desc')}</p>
                        </div>
                        <div>
                            <div className="flex items-center justify-between gap-2"><strong className="text-sm">{t('cookies.analytics')}</strong><Switch checked={analytics} onCheckedChange={setAnalytics} aria-label={t('cookies.analytics')} /></div>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">{t('cookies.analytics_desc')}</p>
                        </div>
                        <div>
                            <div className="flex items-center justify-between gap-2"><strong className="text-sm">{t('cookies.marketing')}</strong><Switch checked={marketing} onCheckedChange={setMarketing} aria-label={t('cookies.marketing')} /></div>
                            <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">{t('cookies.marketing_desc')}</p>
                        </div>
                    </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2 sm:justify-end">
                    {!showPreferences && <Button type="button" variant="outline" onClick={openPreferences}><Settings2 /> {t('cookies.configure')}</Button>}
                    <Button type="button" variant="outline" onClick={() => saveConsent({ analytics: false, marketing: false })}>{t('cookies.reject')}</Button>
                    {showPreferences && <Button type="button" onClick={() => saveConsent({ analytics, marketing })} className="bg-[color:var(--alb-aqua)] text-[color:var(--alb-ink)]">{t('cookies.save')}</Button>}
                    <Button type="button" onClick={() => saveConsent({ analytics: true, marketing: true })} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">{t('cookies.accept')}</Button>
                </div>
            </div>
        </div>
    );
}